import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { DailyLogCreateInput, DailyLogUpdateInput } from '@/hooks/useDailyLogCreate';
import DailyLogReviewModal from '@/components/feature/DailyLogReviewModal';

export interface NewUploadFile {
  id: string;
  file: File;
  previewUrl: string;
  caption: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  errorMsg: string;
  uploadedRecordId?: string;
  uploadedUrl?: string;
}

export interface ExistingPhoto {
  id: string;
  file_url: string;
  caption: string | null;
  file_name: string;
  uploaded_by_name: string | null;
}

/** For edit mode — pre-filled values from an existing daily log record */
export interface DailyLogInitialValues {
  logId: string;
  projectId: string;
  logDate: string;
  title: string;
  managementComment: string;
  internalNote: string;
  visibility: 'internal' | 'client';
  isPublishedToClient: boolean;
  existingLinkedPhotos?: ExistingPhoto[];
}

export interface CreateDailyLogModalProps {
  open: boolean;
  onClose: () => void;
  /** 'create' (default) or 'edit' */
  mode?: 'create' | 'edit';
  /** When mode='edit', the log id being edited */
  editLogId?: string;
  /** Pre-fill values for edit mode */
  initialValues?: DailyLogInitialValues;
  fixedProjectId?: string;
  projects: Array<{ id: string; project_name: string; project_code?: string }>;
  existingPhotos: ExistingPhoto[];
  onRemoveExistingPhoto?: (id: string) => void;
  onSubmit: (payload: {
    input: DailyLogCreateInput;
    newFiles: NewUploadFile[];
    existingPhotoIds: string[];
  }) => Promise<{ success: boolean; error?: string }>;
  /** Called when mode='edit' */
  onSubmitEdit?: (payload: {
    input: DailyLogUpdateInput;
    newFiles: NewUploadFile[];
  }) => Promise<{ success: boolean; error?: string }>;
  loading: boolean;
}

const MB = 1024 * 1024;
const MAX_FILE_SIZE = 20 * MB;
const MAX_TOTAL_SIZE = 100 * MB;
const MAX_FILES = 20;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

function generateId(): string {
  return `uf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getFileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() || 'other';
}

export default function CreateDailyLogModal({
  open,
  onClose,
  mode = 'create',
  editLogId,
  initialValues,
  fixedProjectId,
  projects,
  existingPhotos,
  onRemoveExistingPhoto,
  onSubmit,
  onSubmitEdit,
  loading,
}: CreateDailyLogModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [projectId, setProjectId] = useState('');
  const [logDate, setLogDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'client'>('client');

  const [newFiles, setNewFiles] = useState<NewUploadFile[]>([]);
  const [showReview, setShowReview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [globalError, setGlobalError] = useState('');

  // Edit mode: existing linked photos loaded from DB
  const [linkedPhotos, setLinkedPhotos] = useState<ExistingPhoto[]>([]);
  const [linkedPhotosLoading, setLinkedPhotosLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const resolvedProjectId = fixedProjectId || projectId;
  const fixedProjectName = useMemo(
    () => projects.find((p) => p.id === fixedProjectId)?.project_name,
    [projects, fixedProjectId]
  );

  // Reset / pre-fill form fields whenever the modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialValues) {
        // Pre-fill from initialValues
        setProjectId(initialValues.projectId || '');
        setLogDate(initialValues.logDate || new Date().toISOString().split('T')[0]);
        setTitle(initialValues.title || '');
        setComment(initialValues.managementComment || '');
        setInternalNote(initialValues.internalNote || '');
        setVisibility(initialValues.visibility || 'client');
        if (initialValues.existingLinkedPhotos) {
          setLinkedPhotos(initialValues.existingLinkedPhotos);
        }
        setGlobalError('');

        if (editLogId && !initialValues.existingLinkedPhotos) {
          setLinkedPhotosLoading(true);
          supabase
            .from('jphph_daily_log_photos')
            .select('id, photo_url, caption, source_photo_id')
            .eq('daily_log_id', editLogId)
            .order('created_at', { ascending: true })
            .then(({ data }) => {
              const photos: ExistingPhoto[] = (data || []).map((p: {
                id: string; photo_url: string; caption: string | null; source_photo_id: string | null
              }) => ({
                id: p.id,
                file_url: p.photo_url,
                caption: p.caption,
                file_name: p.caption || 'Photo',
                uploaded_by_name: null,
              }));
              setLinkedPhotos(photos);
              setLinkedPhotosLoading(false);
            })
            .catch(() => setLinkedPhotosLoading(false));
        }
      } else {
        // Create mode — clear everything
        setProjectId('');
        setLogDate(new Date().toISOString().split('T')[0]);
        setTitle('');
        setComment('');
        setInternalNote('');
        setVisibility('client');
        setLinkedPhotos([]);
        setGlobalError('');
      }
      // modal opened
    }
    // Only run when open toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const photoCount = useMemo(
    () => existingPhotos.length + newFiles.filter((f) => f.status !== 'error').length,
    [existingPhotos, newFiles]
  );

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const added: NewUploadFile[] = [];
      let totalSize = newFiles.reduce((s, it) => s + it.file.size, 0);

      for (const file of Array.from(files)) {
        if (newFiles.length + added.length >= MAX_FILES) {
          setGlobalError(`Maximum ${MAX_FILES} photos allowed`);
          break;
        }
        if (file.size > MAX_FILE_SIZE) {
          setGlobalError(`${file.name} exceeds ${formatBytes(MAX_FILE_SIZE)}`);
          continue;
        }
        const ext = getFileExt(file.name);
        const allowed = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
        if (!allowed.includes(ext) && !file.type.startsWith('image/')) {
          setGlobalError(`${file.name}: only images are allowed`);
          continue;
        }
        if (totalSize + file.size > MAX_TOTAL_SIZE) {
          setGlobalError(`Total size exceeds ${formatBytes(MAX_TOTAL_SIZE)}`);
          break;
        }

        added.push({
          id: generateId(),
          file,
          previewUrl: URL.createObjectURL(file),
          caption: '',
          status: 'pending',
          errorMsg: '',
        });
        totalSize += file.size;
      }

      setNewFiles((prev) => [...prev, ...added]);
      if (added.length > 0) setGlobalError('');
    },
    [newFiles]
  );

  const removeNewFile = useCallback((id: string) => {
    setNewFiles((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const removeExistingPhoto = useCallback((id: string) => {
    // Parent handles this by filtering existingPhotos prop
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const resetForm = useCallback(() => {
    newFiles.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setNewFiles([]);
    setLinkedPhotos([]);
    setProjectId('');
    setLogDate(new Date().toISOString().split('T')[0]);
    setTitle('');
    setComment('');
    setInternalNote('');
    setVisibility('client');
    setShowReview(false);
    setGlobalError('');
  }, [newFiles]);

  const handleClose = useCallback(() => {
    if (loading) return;
    resetForm();
    onClose();
  }, [loading, onClose, resetForm]);

  async function confirmSubmit() {
    if (!title.trim()) {
      setGlobalError(t('dailyLog.toastTitleRequired') || 'Please enter a title');
      return;
    }
    if (!resolvedProjectId) {
      setGlobalError('Please select a project');
      return;
    }

    setGlobalError('');

    // Edit mode
    if (mode === 'edit' && editLogId) {
      if (!onSubmitEdit) {
        setGlobalError('Edit handler not configured');
        return;
      }
      try {
        const result = await onSubmitEdit({
          input: {
            logId: editLogId,
            project_id: resolvedProjectId,
            log_date: logDate,
            title: title.trim(),
            management_comment: comment.trim() || undefined,
            internal_note: internalNote.trim() || undefined,
            visibility,
            is_published_to_client: visibility === 'client',
            created_by: user?.id || '',
          },
          newFiles,
        });

        if (result.success) {
          newFiles.forEach((f) => {
            if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
          });
          setNewFiles([]);
          setLinkedPhotos([]);
          setShowReview(false);
          onClose();
        } else {
          setGlobalError(result.error || 'Failed to update Daily Log');
        }
      } catch (submitErr) {
        const msg = submitErr instanceof Error ? submitErr.message : String(submitErr);
        setGlobalError(msg || 'An unexpected error occurred while updating the Daily Log');
      }
      return;
    }

    // Create mode
    const existingPhotoIds = existingPhotos.map((p) => p.id);

    try {
      const result = await onSubmit({
        input: {
          project_id: resolvedProjectId,
          log_date: logDate,
          title: title.trim(),
          management_comment: comment.trim() || undefined,
          internal_note: internalNote.trim() || undefined,
          visibility,
          is_published_to_client: visibility === 'client',
          selected_photo_ids: existingPhotoIds,
          created_by: user?.id || '',
        },
        newFiles,
        existingPhotoIds,
      });

      if (result.success) {
        resetForm();
        setShowReview(false);
        onClose();
      } else {
        setGlobalError(result.error || 'Failed to create Daily Log');
      }
    } catch (submitErr) {
      const msg = submitErr instanceof Error ? submitErr.message : String(submitErr);
      setGlobalError(msg || 'An unexpected error occurred while creating the Daily Log');
    }
  }

  function handlePrimaryAction() {
    if (!title.trim()) {
      setGlobalError(t('dailyLog.toastTitleRequired') || 'Please enter a title');
      return;
    }
    if (!resolvedProjectId) {
      setGlobalError('Please select a project');
      return;
    }
    if (visibility === 'client') {
      setShowReview(true);
      return;
    }
    void confirmSubmit();
  }

  async function handleSubmit() {
    handlePrimaryAction();
  }

  const reviewPhotos = useMemo(() => {
    const selected = existingPhotos.map((p) => ({
      id: p.id,
      url: p.file_url,
      caption: p.caption,
      fileName: p.file_name,
    }));
    const uploaded = newFiles
      .filter((f) => f.status !== 'error')
      .map((f) => ({
        id: f.id,
        url: f.previewUrl,
        caption: f.caption,
        fileName: f.file.name,
      }));
    const linked = linkedPhotos.map((p) => ({
      id: p.id,
      url: p.file_url,
      caption: p.caption,
      fileName: p.file_name,
    }));
    return [...linked, ...selected, ...uploaded];
  }, [existingPhotos, newFiles, linkedPhotos]);

  if (!open) return null;

  const pendingNewCount = newFiles.filter((f) => f.status === 'pending').length;
  const hasNewError = newFiles.some((f) => f.status === 'error');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white w-full max-w-lg rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#1A1A1A] font-bold text-base">
            {mode === 'edit' ? 'Edit Daily Log' : t('dailyLog.createDailyLog')}
          </h3>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Project field — fixed or selector */}
          {fixedProjectId ? (
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1">Project</label>
              <div className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] bg-gray-50 flex items-center gap-2">
                <i className="ri-building-line text-[#9CA3AF]" />
                <span className="font-medium">{fixedProjectName || 'Selected project'}</span>
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-1">
                Project is locked because a project filter is active on the review page. Clear the filter to choose a different project.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1">Project *</label>
              {projects.length === 0 ? (
                <div className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#9CA3AF] bg-gray-50 flex items-center gap-2">
                  <i className="ri-error-warning-line text-[#F97316]" />
                  <span>No projects available — try reloading the page</span>
                </div>
              ) : (
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] bg-white focus:outline-none focus:border-[#E8621A] cursor-pointer"
                >
                  <option value="">{t('dailyLog.selectProject') || 'Select project'}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1">Date</label>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1">{t('dailyLog.titleRequired') || 'Title *'}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('dailyLog.titlePlaceholder') || 'Daily Log title'}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A]"
            />
          </div>

          {/* Management Comment */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1">{t('dailyLog.managementComment') || 'Comment'}</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('dailyLog.managementComment') || 'Add a comment...'}
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#E8621A]"
            />
          </div>

          {/* Internal Note */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1">
              <i className="ri-lock-line mr-0.5 text-[#F97316]" />Internal Note (management only)
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Add an internal note visible only to management..."
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#E8621A]"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1">{t('dailyLog.visibility') || 'Visibility'}</label>
            <div className="flex gap-2">
              {([
                ['client', t('dailyLog.shareWithClient') || 'Share with Client', 'ri-eye-line'],
                ['internal', t('dailyLog.internalOnly') || 'Internal Only', 'ri-eye-off-line'],
              ] as const).map(([val, label, icon]) => (
                <button
                  key={val}
                  onClick={() => setVisibility(val)}
                  className={`flex-1 flex items-center gap-2 py-2.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap border-2 transition-colors ${
                    visibility === val
                      ? val === 'client'
                        ? 'border-[#059669] text-[#059669] bg-[#F0FDF4]'
                        : 'border-[#374151] text-[#374151] bg-[#F3F4F6]'
                      : 'border-gray-100 text-[#9CA3AF] bg-gray-50'
                  }`}
                >
                  <i className={`${icon} text-sm`} />{label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Photos Section ── */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[#555555] text-xs font-semibold">Photos</label>
              {photoCount > 0 && (
                <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full">
                  {photoCount} photo{photoCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />

            {/* Desktop: drag & drop zone */}
            {!isMobile && (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors mb-3 ${
                  isDragging
                    ? 'border-[#E8621A] bg-[#FEF0E7]/40'
                    : 'border-gray-200 hover:border-[#E8621A] bg-[#FAFAFA]'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-[#9CA3AF]">
                  <i className="ri-upload-cloud-2-line text-3xl" />
                </div>
                <p className="text-[#374151] text-sm font-medium">Drag photos here or click to upload</p>
                <p className="text-[#9CA3AF] text-xs mt-0.5">JPG, JPEG, PNG, WEBP · Max {formatBytes(MAX_FILE_SIZE)} each</p>
              </div>
            )}

            {/* Mobile: Take Photo + Choose Photo buttons */}
            {isMobile && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FEF0E7]">
                    <i className="ri-camera-line text-[#E8621A] text-lg" />
                  </div>
                  <span className="text-[#374151] text-[11px] font-semibold">Take Photo</span>
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FEF0E7]">
                    <i className="ri-image-line text-[#E8621A] text-lg" />
                  </div>
                  <span className="text-[#374151] text-[11px] font-semibold">Choose Photo</span>
                </button>
              </div>
            )}

            {/* Edit mode: existing linked photos (read-only) */}
            {mode === 'edit' && (linkedPhotosLoading ? (
              <div className="flex items-center gap-2 text-[#9CA3AF] text-xs py-2">
                <i className="ri-loader-4-line animate-spin" />
                Loading existing photos...
              </div>
            ) : linkedPhotos.length > 0 ? (
              <div className="mb-3">
                <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                  Existing photos ({linkedPhotos.length}) — read-only
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {linkedPhotos.map((photo) => (
                    <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-100">
                      <img src={photo.file_url} alt={photo.caption || photo.file_name} className="w-full h-full object-cover object-top" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                        <p className="text-white text-[9px] truncate">{photo.file_name || photo.caption || 'Photo'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[#9CA3AF] text-[10px] mt-1.5">
                  <i className="ri-information-line mr-0.5" />
                  Existing photos are preserved. Use the upload area below to add new photos.
                </p>
              </div>
            ) : null)}

            {/* Create mode: selected existing photos from review grid */}
            {mode === 'create' && existingPhotos.length > 0 && (
              <div className="mb-3">
                <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                  Selected from review ({existingPhotos.length})
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {existingPhotos.map((photo) => (
                    <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-100 group">
                      <img src={photo.file_url} alt={photo.caption || photo.file_name} className="w-full h-full object-cover object-top" />
                      {onRemoveExistingPhoto && (
                        <button
                          onClick={() => onRemoveExistingPhoto(photo.id)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Remove from selection"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                        <p className="text-white text-[9px] truncate">{photo.file_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edit mode label when no linked photos */}
            {mode === 'edit' && !linkedPhotosLoading && linkedPhotos.length === 0 && (
              <p className="text-[#9CA3AF] text-xs text-center py-1 mb-2">
                <i className="ri-image-line mr-1" />
                No photos attached to this log. Upload new photos below.
              </p>
            )}

            {/* New upload previews */}
            {newFiles.length > 0 && (
              <div>
                <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                  New uploads ({newFiles.filter((f) => f.status !== 'error').length})
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {newFiles.map((file) => (
                    <div key={file.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-100 group">
                      <img src={file.previewUrl} alt={file.file.name} className="w-full h-full object-cover object-top" />
                      {file.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin text-white text-lg" />
                        </div>
                      )}
                      {file.status === 'error' && (
                        <div className="absolute inset-0 bg-[#EF4444]/60 flex items-center justify-center">
                          <i className="ri-error-warning-line text-white text-lg" />
                        </div>
                      )}
                      <button
                        onClick={() => removeNewFile(file.id)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Remove"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                        <p className="text-white text-[9px] truncate">{file.file.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload summary when nothing selected */}
            {existingPhotos.length === 0 && newFiles.length === 0 && linkedPhotos.length === 0 && mode === 'create' && (
              <p className="text-[#9CA3AF] text-xs text-center py-2">
                <i className="ri-image-line mr-1" />
                No photos added yet. Upload new photos above, or select from the review grid before opening this modal.
              </p>
            )}
          </div>

          {/* Global error */}
          {globalError && (
            <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5" />
              <p className="text-[#EF4444] text-xs">{globalError}</p>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 mt-5">
          <button onClick={handleClose} className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap">
            {t('dailyLog.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handlePrimaryAction}
            disabled={loading || !title.trim() || (!fixedProjectId && !projectId)}
            className="flex-1 py-3 rounded-xl bg-[#059669] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><i className="ri-loader-4-line animate-spin" />{mode === 'edit' ? 'Saving...' : (t('dailyLog.creating') || 'Creating...')}</>
            ) : visibility === 'client' ? (
              <>
                <i className="ri-eye-line" />
                Review &amp; Share with Client
              </>
            ) : mode === 'edit' ? (
              <><i className="ri-save-line" />Save Changes</>
            ) : (
              <>
                <i className="ri-book-open-line" />
                {t('dailyLog.createDailyLog') || 'Create Daily Log'}
                {photoCount > 0 && <span className="ml-1 text-[10px] opacity-80">({photoCount})</span>}
              </>
            )}
          </button>
        </div>
      </div>

      <DailyLogReviewModal
        open={showReview}
        title={mode === 'edit' ? 'Review Daily Log Changes' : 'Review Daily Log for Client'}
        subtitle="Confirm what will be shared with the client before publishing."
        projectName={fixedProjectName || projects.find((p) => p.id === resolvedProjectId)?.project_name}
        logDate={logDate}
        bodyTitle={title.trim() || 'Daily Log'}
        bodyText={[comment.trim(), internalNote.trim() ? `Internal note (not shared): ${internalNote.trim()}` : '']
          .filter(Boolean)
          .join('\n\n')}
        photos={reviewPhotos}
        visibilityLabel="Share with Client"
        confirmLabel={mode === 'edit' ? 'Save & Share with Client' : 'Create & Share with Client'}
        cancelLabel="Back / Edit"
        loading={loading}
        onCancel={() => setShowReview(false)}
        onConfirm={() => void confirmSubmit()}
      />
    </div>
  );
}